import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  Check,
  DollarSign,
  DownloadCloud,
  Globe,
  Key,
  Link as LinkIcon,
  LogOut,
  RefreshCw,
  UploadCloud,
  User,
} from "lucide-react";
import { apiFetch } from "@/lib/apiClient";
import { RequireRole } from "@/lib/auth";
import { PrimaryButton } from "@/components/PrimaryButton";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/settings/ebay")({
  component: () => (
    <ManagerOnly>
      <EbayConnectPage />
    </ManagerOnly>
  ),
});

type StatusData =
  | {
      connected: false;
      environment?: string;
      reason?: string;
      ebayUsername?: string | null;
    }
  | {
      connected: true;
      environment: string;
      accessTokenExpiry: string;
      refreshTokenExpiry: string;
      autoRefreshed?: boolean;
      ebayUsername?: string | null;
    };

async function fetchStatus(): Promise<StatusData> {
  const res = await apiFetch("/api/ebay/auth/status");
  if (!res.ok) throw new Error(`Status check failed: ${res.status}`);
  return res.json() as Promise<StatusData>;
}

async function fetchAuthUrl(): Promise<string> {
  const res = await apiFetch("/api/ebay/auth/url");
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "Failed to get auth URL");
  }
  const data = (await res.json()) as { url: string };
  return data.url;
}

async function disconnect(): Promise<void> {
  const res = await apiFetch("/api/ebay/auth/disconnect", { method: "DELETE" });
  if (!res.ok) throw new Error("Disconnect failed");
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffMs < 0) return "Expired";
  if (diffDays > 1) return `${diffDays} days`;
  if (diffHours > 0) return `${diffHours} hours`;
  return "< 1 hour";
}

function EbayConnectPage() {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadStatus = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const s = await fetchStatus();
      setStatus(s);
    } catch {
      setError("Could not reach the server. Is the API server running?");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    let elapsed = 0;
    const MAX_POLL_MS = 3 * 60 * 1000;
    const INTERVAL_MS = 2500;
    pollRef.current = setInterval(async () => {
      elapsed += INTERVAL_MS;
      if (elapsed >= MAX_POLL_MS) {
        if (pollRef.current) clearInterval(pollRef.current);
        setConnecting(false);
        setError(
          "Connection timed out. Complete authorization in the new tab, then tap 'Refresh Status'.",
        );
        return;
      }
      try {
        const s = await fetchStatus();
        if (s.connected) {
          setStatus(s);
          setConnecting(false);
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        // ignore poll errors — keep retrying until MAX_POLL_MS
      }
    }, INTERVAL_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    try {
      const url = await fetchAuthUrl();
      window.open(url, "_blank", "noopener,noreferrer");
      startPolling();
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Failed to start eBay connection";
      setError(msg);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setConfirmDisconnect(false);
    try {
      await disconnect();
      await loadStatus(true);
    } catch {
      setError("Failed to disconnect. Please try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  const isConnected = status?.connected === true;
  const callbackUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/ebay/oauth/callback`
      : "<your-domain>/api/ebay/oauth/callback";

  return (
    <section className="flex h-full flex-col">
      <div className="space-y-2 border-b border-border pb-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">eBay Integration</h1>
          {status?.environment && (
            <span
              className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
                status.environment === "production"
                  ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                  : "border-amber-600 bg-amber-50 text-amber-700"
              }`}
            >
              {status.environment === "production" ? "LIVE" : "SANDBOX"}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Connect your eBay seller account to list cards and sync sales.
        </p>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Status card */}
        <div className="rounded-md bg-primary p-4 text-primary-foreground">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="text-[10px] font-medium tracking-wider opacity-75">
                CONNECTION STATUS
              </div>
              {loading ? (
                <div className="flex items-center gap-2 text-sm opacity-75">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Checking…
                </div>
              ) : (
                <div
                  className={`text-2xl font-bold ${
                    isConnected ? "text-secondary" : "text-red-300"
                  }`}
                >
                  {isConnected ? "Connected" : "Not Connected"}
                </div>
              )}
            </div>
            <span
              className={`mt-1 h-3 w-3 rounded-full ${
                loading
                  ? "bg-muted-foreground"
                  : isConnected
                    ? "bg-emerald-400"
                    : "bg-red-400"
              }`}
            />
          </div>

          {isConnected && status?.connected === true && (
            <div className="mt-3 space-y-1.5 border-t border-primary-foreground/20 pt-3">
              {status.ebayUsername && (
                <StatusRow
                  Icon={User}
                  label="eBay account"
                  value={status.ebayUsername}
                  valueColor="text-secondary"
                />
              )}
              <StatusRow
                Icon={Globe}
                label="Environment"
                value={
                  status.environment === "sandbox"
                    ? "Sandbox (Testing)"
                    : "LIVE (Production)"
                }
                valueColor="text-secondary"
              />
              <StatusRow
                Icon={Key}
                label="Access token"
                value={`Expires in ${formatExpiry(status.accessTokenExpiry)}`}
              />
              <StatusRow
                Icon={RefreshCw}
                label="Refresh token"
                value={`Expires in ${formatExpiry(status.refreshTokenExpiry)}`}
              />
            </div>
          )}
        </div>

        {/* Connecting state */}
        {connecting && (
          <div className="flex items-start gap-3 rounded-md border border-secondary/40 bg-card p-3">
            <span className="mt-0.5 inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-secondary border-t-transparent" />
            <div className="space-y-0.5">
              <div className="text-sm font-semibold text-foreground">
                Waiting for eBay authorization…
              </div>
              <p className="text-xs text-muted-foreground">
                Complete sign-in in the new tab, then return here. We'll detect
                the connection automatically.
              </p>
            </div>
          </div>
        )}

        {/* Connect actions */}
        {!isConnected && !connecting && (
          <div className="space-y-3">
            {/* Connect is Owner-only (TNK-81). Managers can use the store's
                connection but not link/unlink it; Check Status stays available. */}
            <RequireRole
              min="owner"
              fallback={
                <div className="rounded-md border border-border bg-card p-3 text-sm text-muted-foreground">
                  No eBay account is connected. Ask the store owner to connect
                  one in this screen.
                </div>
              }
            >
              <PrimaryButton
                label="Connect eBay Account"
                onClick={handleConnect}
                variant="secondary"
                leadingIcon={<LinkIcon className="h-4 w-4" />}
              />
            </RequireRole>
            <button
              type="button"
              onClick={() => loadStatus()}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className="h-4 w-4" />
              Check Status
            </button>

            <RequireRole min="owner">
              <div className="rounded-md bg-card p-4">
                <div className="mb-2 text-[10px] font-medium tracking-wider text-muted-foreground">
                  SETUP REQUIRED
                </div>
                <CheckItem
                  text='In the eBay Developer Portal, set this exact URL as the "Auth Accepted URL" for your RuName:'
                  detail={callbackUrl}
                />
                {status?.environment === "sandbox" ? (
                  <CheckItem text="Sign in with a Sandbox test account — not your real eBay account" />
                ) : (
                  <CheckItem text="Sign in with your real eBay seller account — listings will go live on ebay.com" />
                )}
              </div>
            </RequireRole>
          </div>
        )}

        {/* Disconnect actions */}
        {isConnected && !connecting && (
          <div className="space-y-3">
            <RequireRole min="owner">
              {confirmDisconnect ? (
                <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Disconnect your eBay account?
                  </div>
                  <p className="text-xs text-destructive/80">
                    You'll need to re-authorize before listing cards or syncing
                    eBay sales again.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDisconnect(false)}
                      className="flex-1 rounded-md border border-destructive px-3 py-2 text-sm font-semibold text-destructive hover:bg-destructive/5"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      className="flex-1 rounded-md bg-destructive px-3 py-2 text-sm font-semibold text-destructive-foreground hover:opacity-90 disabled:opacity-60"
                    >
                      {disconnecting ? "Disconnecting…" : "Disconnect"}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDisconnect(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-destructive px-4 py-3 text-sm font-semibold text-destructive hover:bg-destructive/5"
                >
                  <LogOut className="h-4 w-4" />
                  Disconnect
                </button>
              )}
            </RequireRole>
            <button
              type="button"
              onClick={() => loadStatus()}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh Status
            </button>
          </div>
        )}

        {/* What this enables */}
        <div className="rounded-md bg-card p-4">
          <div className="mb-2 text-[10px] font-medium tracking-wider text-muted-foreground">
            WHAT THIS ENABLES
          </div>
          <ul className="space-y-2">
            <InfoRow
              Icon={UploadCloud}
              text="List cards directly to eBay from your inventory"
            />
            <InfoRow
              Icon={DownloadCloud}
              text="Sync completed eBay sales back into the Sales tab"
            />
            <InfoRow
              Icon={DollarSign}
              text="Track eBay fees and net profit automatically"
            />
          </ul>
        </div>
      </div>
    </section>
  );
}

function StatusRow({
  Icon,
  label,
  value,
  valueColor,
}: {
  Icon: typeof User;
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs">
      <div className="flex items-center gap-1.5 opacity-75">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <span
        className={`font-semibold ${valueColor ?? "text-primary-foreground"}`}
      >
        {value}
      </span>
    </div>
  );
}

function CheckItem({ text, detail }: { text: string; detail?: string }) {
  return (
    <div className="mb-2 flex items-start gap-2 last:mb-0">
      <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs text-foreground">{text}</p>
        {detail && (
          <code className="block break-all rounded bg-muted px-2 py-1 text-[10px] text-muted-foreground">
            {detail}
          </code>
        )}
      </div>
    </div>
  );
}

function InfoRow({ Icon, text }: { Icon: typeof User; text: string }) {
  return (
    <li className="flex items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-secondary" />
      <span className="text-sm text-foreground">{text}</span>
    </li>
  );
}
